import {
  FC,
  useState,
  CSSProperties,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { UnorderedListOutlined } from '@ant-design/icons';
import { ProSelect, ProTag } from '../antd';
import classnames from 'classnames';
import { stringSubstr } from '../utils/dataUtils';
import {
  SUCCESS_STATUS_CODE,
  ASSOCIATE_INPUT_SPLIT,
  TABLE_COLUMN_EMPTY,
  MAX_TAG_TEXT_LENGTH,
} from '../constants/constant';
import './style.less';

type valueType = string | string[] | string[][];
type modeType = 'multiple' | 'tags';
export interface RuleProps {
  prefix?: string;
  label?: string;
  value?: string;
}
/** 自动匹配下拉列表 */
export interface AssociateSelectProps {
  /** 自定义类名 */
  className?: string;
  /** 下拉菜单的 className 属性 */
  dropdownClassName?: string;
  /** 选择框默认文本 */
  placeholder?: string;
  /** 样式 */
  style?: CSSProperties;
  /** 是否显示下拉小箭头 */
  showArrow?: boolean;
  /** 匹配规则 */
  rules?: RuleProps[];
  /** 匹配规则参数 */
  defaultParams?: any;
  /** 加载中状态 */
  loading?: boolean;
  /** 支持清除 */
  allowClear?: boolean;
  /** 指定当前选中的条目 */
  value?: valueType;
  /** 设置 Select 的模式为多选或标签 */
  mode?: modeType;
  /** 禁用 */
  disabled?: boolean;
  /** 是否为不可编辑的详情展示 */
  isDetail?: boolean;
  /** label对应字段名 */
  labelName?: string;
  /** value对应字段名 */
  valueName?: string;
  /** prefix对应字段名 */
  prefixName?: string;
  /** 分隔符 */
  splitSymbol?: string;
  /** 后缀图标 */
  suffixIcon?: ReactNode;
  /** 最多显示多少个 tag */
  maxTagCount?: number;
  /** 最大显示的 tag 文本长度 */
  maxTagTextLength?: number;
  /** 远程获取匹配规则 */
  fetchApi?: (params?: any) => Promise<any>;
  /** 数据处理 */
  onBeforeData?: (data: any) => RuleProps[];
  /** 选中 option，或 input 的 value 变化时，调用此函数 */
  onChange?: (value?: string[]) => void;
}

const formatRuleData = (
  data: any,
  labelName: string,
  valueName: string,
  prefixName: string,
): RuleProps[] => {
  let ruleData: RuleProps[] = [];
  if (Array.isArray(data)) {
    ruleData = data.map(item => {
      return {
        prefix: item[prefixName],
        label: item[labelName],
        value: item[valueName],
      };
    });
  }
  return ruleData;
};
const formatOption = (
  value?: valueType,
  currentRules?: RuleProps[],
  options?: ReactNode,
  setOptions?: React.Dispatch<React.SetStateAction<ReactNode>>,
  maxTagTextLength?: number,
) => {
  if (value && currentRules && !options && setOptions) {
    let newOptions: ReactNode = null;
    if (typeof value === 'string') {
      const vals: string[] = value.split(ASSOCIATE_INPUT_SPLIT);
      if (vals.length === 2) {
        const [beforeValue, afterValue] = vals;
        const findRule = currentRules.find(rule => rule.value === beforeValue);
        if (findRule) {
          const optionLabel = `${
            findRule.label ? `${findRule.label}：` : null
          }${afterValue}`;
          newOptions = (
            <ProSelect.Option
              key={findRule.label}
              value={`${findRule.value}${ASSOCIATE_INPUT_SPLIT}${afterValue}`}
              label={
                <span
                  title={optionLabel}
                  className="yb-select-option-label yb-line-ellipsis"
                  onClick={event => {
                    event.stopPropagation();
                  }}
                  onMouseDown={event => {
                    event.stopPropagation();
                  }}
                >
                  {optionLabel}
                </span>
              }
            >
              {findRule.label ? `${findRule.label}：` : null}
              {afterValue}
            </ProSelect.Option>
          );
        }
      }
    } else if (Array.isArray(value)) {
      newOptions = value.map(item => {
        const vals: string[] = (item as string).split?.(ASSOCIATE_INPUT_SPLIT);
        if (vals.length === 2) {
          const [beforeValue, afterValue] = vals;
          const findRule = currentRules.find(
            rule => rule.value === beforeValue,
          );
          if (findRule) {
            const optionLabel = `${
              findRule.label ? `${findRule.label}：` : null
            }${afterValue}`;
            return (
              <ProSelect.Option
                key={findRule.label}
                value={`${findRule.value}${ASSOCIATE_INPUT_SPLIT}${afterValue}`}
                label={
                  <span
                    title={optionLabel}
                    className="yb-select-option-label yb-line-ellipsis"
                    onClick={event => {
                      event.stopPropagation();
                    }}
                    onMouseDown={event => {
                      event.stopPropagation();
                    }}
                  >
                    {stringSubstr(optionLabel, maxTagTextLength)}
                  </span>
                }
              >
                {findRule.label ? `${findRule.label}：` : null}
                {afterValue}
              </ProSelect.Option>
            );
          }
        }
        return null;
      });
    }
    setOptions(newOptions);
  }
};
const formatValue = (value?: valueType, mode?: modeType) => {
  let newValue = value;
  if (Array.isArray(newValue)) {
    if (mode === 'multiple') {
      newValue = newValue.map(v => {
        if (Array.isArray(v)) return v.join(ASSOCIATE_INPUT_SPLIT);
        return v;
      });
    } else {
      newValue = newValue.join(ASSOCIATE_INPUT_SPLIT);
    }
  }
  return newValue;
};
/**
 * 下拉自动匹配下拉列表
 */
export const AssociateSelect: FC<AssociateSelectProps> = props => {
  const {
    className,
    dropdownClassName,
    placeholder,
    style,
    showArrow,
    rules,
    disabled,
    fetchApi,
    isDetail,
    onBeforeData,
    defaultParams,
    mode,
    allowClear,
    onChange,
    value,
    splitSymbol,
    suffixIcon,
    maxTagTextLength,
    valueName = 'value',
    labelName = 'label',
    prefixName = 'prefix',
    ...restProps
  } = props;
  const [currentRules, setCurrentRules] = useState<RuleProps[] | undefined>(
    rules,
  );
  const [currentValue, setCurrentValue] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<ReactNode>(null);
  const selectRef = useRef<any>(null);
  useEffect(() => {
    setCurrentValue(formatValue(value, mode));
    if (value && !currentValue) {
      if (fetchApi) {
        fetch();
      } else {
        formatOption(
          formatValue(value, mode) || currentValue,
          currentRules,
          options,
          setOptions,
          maxTagTextLength,
        );
      }
    }
  }, [value]);
  const fetch = () => {
    if (!fetchApi) return;
    setLoading(true);
    fetchApi?.(defaultParams).then(res => {
      if (res.code == SUCCESS_STATUS_CODE) {
        const data: RuleProps[] =
          onBeforeData?.(res.data) ||
          formatRuleData(res.data, labelName, valueName, prefixName);
        setCurrentRules(data);
        formatOption(
          formatValue(value, mode) || currentValue,
          data,
          options,
          setOptions,
        );
      }
      setLoading(false);
    });
  };
  const handleSearch = (newValue: string) => {
    let newOptions: ReactNode[] = [];
    if (newValue) {
      const findRule = currentRules?.find(item => {
        if (!item.prefix) return false;
        return newValue.startsWith(item.prefix);
      });
      if (findRule) {
        const optionLabel = `${
          findRule.label ? `${findRule.label}：` : null
        }${newValue}`;
        const newOption = (
          <ProSelect.Option
            value={`${findRule.value}${ASSOCIATE_INPUT_SPLIT}${newValue}`}
            key={`${findRule.value}${ASSOCIATE_INPUT_SPLIT}${newValue}`}
            label={
              <span
                title={optionLabel}
                className="yb-select-option-label"
                onClick={event => {
                  event.stopPropagation();
                }}
                onMouseDown={event => {
                  event.stopPropagation();
                }}
              >
                {mode === 'multiple'
                  ? stringSubstr(optionLabel, maxTagTextLength)
                  : optionLabel}
              </span>
            }
          >
            <span title={optionLabel}>{optionLabel}</span>
          </ProSelect.Option>
        );
        newOptions.push(newOption);
      } else {
        currentRules?.forEach(item => {
          const optionLabel = `${
            item.label ? `${item.label}：` : null
          }${newValue}`;
          newOptions.push(
            <ProSelect.Option
              value={`${item.value}${ASSOCIATE_INPUT_SPLIT}${newValue}`}
              key={`${item.value}${ASSOCIATE_INPUT_SPLIT}${newValue}`}
              label={
                <span
                  title={optionLabel}
                  className="yb-select-option-label"
                  onClick={event => {
                    event.stopPropagation();
                  }}
                  onMouseDown={event => {
                    event.stopPropagation();
                  }}
                >
                  {mode === 'multiple'
                    ? stringSubstr(optionLabel, maxTagTextLength)
                    : optionLabel}
                </span>
              }
            >
              <span title={optionLabel}>{optionLabel}</span>
            </ProSelect.Option>,
          );
        });
      }
    }
    setOptions(newOptions);
  };
  const handleChange = (val: any) => {
    if (!val || val.length === 0) {
      setOptions([]);
    }
    setCurrentValue(val);
    let newVal: string[] | undefined = undefined;
    // if (!val) return;
    if (typeof val === 'string') {
      newVal = val.split(ASSOCIATE_INPUT_SPLIT);
    }
    if (Array.isArray(val)) {
      newVal = val.map(v => v.split(ASSOCIATE_INPUT_SPLIT));
    }
    onChange?.(newVal);
  };
  const handleFocus = () => {
    if (!currentRules) fetch();
  };
  const handleSuffixIcon = () => {
    selectRef.current?.focus();
  };
  const detailRender = () => {
    if (currentValue && currentRules) {
      let renders: any[] = [];
      if (typeof currentValue === 'string') {
        const vals: string[] = currentValue.split(ASSOCIATE_INPUT_SPLIT);
        const [beforeValue, afterValue] = vals;
        const findRule = currentRules.find(item => item.value === beforeValue);
        if (findRule)
          renders.push(
            `${findRule.label}${splitSymbol}${afterValue ||
              TABLE_COLUMN_EMPTY}`,
          );
      } else if (Array.isArray(currentValue)) {
        renders = currentValue.map(item => {
          const vals: string[] = item.split(ASSOCIATE_INPUT_SPLIT);
          const [beforeValue, afterValue] = vals;
          const findRule = currentRules.find(
            item => item.value === beforeValue,
          );
          if (findRule)
            return `${findRule.label}${splitSymbol}${afterValue ||
              TABLE_COLUMN_EMPTY}`;
        });
      }
      return renders.join('，');
    }
  };
  const classes = classnames('yb-associate-select', className);
  const dropdownClasses = classnames(
    'yb-associate-dropdown-select',
    dropdownClassName,
  );
  return (
    <>
      {isDetail || disabled ? (
        <div style={{ borderBottom: 'dashed 1px #adb5bd' }}>
          {detailRender()}
        </div>
      ) : (
        <ProSelect
          {...restProps}
          className={classes}
          popupClassName={dropdownClasses}
          ref={selectRef}
          showSearch
          allowClear={allowClear}
          placeholder={placeholder}
          style={style}
          optionLabelProp="label"
          disabled={disabled}
          showArrow={showArrow}
          notFoundContent={null}
          onSearch={handleSearch}
          maxTagPlaceholder={omittedValues => {
            return `+${omittedValues.length}`;
          }}
          tagRender={props => {
            const { label, closable, onClose } = props;
            return (
              <ProTag
                closable={closable}
                onClose={onClose}
                style={{ marginRight: 3 }}
                onClick={event => {
                  event.stopPropagation();
                }}
                onMouseDown={event => {
                  event.stopPropagation();
                }}
              >
                {label}
              </ProTag>
            );
          }}
          loading={loading}
          mode={mode}
          onChange={handleChange}
          value={currentValue}
          onFocus={handleFocus}
          suffixIcon={
            suffixIcon ? (
              <span onClick={handleSuffixIcon}>{suffixIcon}</span>
            ) : null
          }
        >
          {options}
        </ProSelect>
      )}
    </>
  );
};

AssociateSelect.defaultProps = {
  style: { width: '100%' },
  showArrow: true,
  allowClear: true,
  isDetail: false,
  splitSymbol: ':',
  // maxTagCount: 1,
  suffixIcon: <UnorderedListOutlined />,
  maxTagTextLength: MAX_TAG_TEXT_LENGTH,
};
export default AssociateSelect;
